import { Text } from '@change/ds-core-ui/Text';
import { Tip } from '@change/ds-core-ui/Tip';
import { Form, TextArea, TextField } from '@change/ds-forms';
import { CheckIcon } from '@change/ds-icons/CheckIcon';
import { LightbulbOutlineIcon } from '@change/ds-icons/LightbulbOutlineIcon';
import { cn } from '@change/ds-utils';

import { translationKey, type TranslationKey } from '@change-corgi/core/i18n';
import { Translate, TypedTranslate, TypedTranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { StepActions } from '#src/app/pages/startAPetitionV2/shared/components';

import { ErrorScreen } from './components/ErrorScreen';
import { Loading } from './components/Loading';
import { usePersonalPrompt } from './hooks/usePersonalPrompt';

type CopyKeys = {
	heading: TranslationKey;
	description: TranslationKey;
	inputLabel: TranslationKey;
	inputPlaceholder: TranslationKey;
};

type VariantContent = {
	copyKeys: CopyKeys;
	showDetailsBox: boolean;
};

const CONTROL_CONTENT: VariantContent = {
	copyKeys: {
		heading: translationKey('corgi.start_a_petition.personal_prompt.heading'),
		description: translationKey('corgi.start_a_petition.personal_prompt.description'),
		inputLabel: translationKey('corgi.start_a_petition.personal_prompt.personal_story_input_label'),
		inputPlaceholder: translationKey('corgi.start_a_petition.personal_prompt.personal_story_input_placeholder'),
	},
	showDetailsBox: false,
};

// CHANGE-95691 bake-off (Figma node 7085-1972): Variant 1/Variant 2 both add the "details that
// make people care" example box below the input — they differ only in heading/description/
// label/placeholder copy (Variant 1: community-focused framing; Variant 2: personal-moment
// framing). Named "Variant 1"/"Variant 2" (not "V1"/"V2") to avoid reading as version numbers.
// showDetailsBox is declared alongside each variant's copy keys (not derived separately) so the
// two can't drift out of sync if a variation is renamed or added.
const VARIANT_CONTENT: Record<'Variant 1' | 'Variant 2', VariantContent> = {
	'Variant 1': {
		copyKeys: {
			heading: translationKey('corgi.start_a_petition.personal_prompt.variant_1.heading'),
			description: translationKey('corgi.start_a_petition.personal_prompt.variant_1.description'),
			inputLabel: translationKey('corgi.start_a_petition.personal_prompt.variant_1.input_label'),
			inputPlaceholder: translationKey('corgi.start_a_petition.personal_prompt.variant_1.input_placeholder'),
		},
		showDetailsBox: true,
	},
	'Variant 2': {
		copyKeys: {
			heading: translationKey('corgi.start_a_petition.personal_prompt.variant_2.heading'),
			description: translationKey('corgi.start_a_petition.personal_prompt.variant_2.description'),
			inputLabel: translationKey('corgi.start_a_petition.personal_prompt.variant_2.input_label'),
			inputPlaceholder: translationKey('corgi.start_a_petition.personal_prompt.variant_2.input_placeholder'),
		},
		showDetailsBox: true,
	},
};

function isKnownVariant(variation: string): variation is keyof typeof VARIANT_CONTENT {
	return variation in VARIANT_CONTENT;
}

/**
 * Looks up the copy keys and layout flag for a variation, falling back to the control content
 * for any unrecognized variation. Uses translationKey()/TypedTranslate here (rather than plain
 * string keys) because the no-invalid-translation-keys lint rule requires literal string
 * arguments to translate()/Translate — the variation-based lookup below returns a variable, not
 * a literal. The non-en-locale gate lives in usePersonalPrompt (shared with exposure-tracking
 * suppression), so by the time `variation` gets here it's already 'Control' for any locale
 * without variant copy.
 */
function getVariantContent(variation: string): VariantContent {
	return isKnownVariant(variation) ? VARIANT_CONTENT[variation] : CONTROL_CONTENT;
}

/**
 * Renders one row of the details-box checklist: a check icon plus the translated row text.
 *
 * The translation string carries `label`/`example` classnames only (no inline styling — see
 * apps/front-end/config/i18n/placeholders/startAPetitionV2/start-a-petition.yml) and this cascade
 * selector decides how they actually look, so design can change the styling without touching the
 * translation. Rendered via TypedTranslateHtml since the string embeds <span> markup; DOMPurify's
 * default allowlist (used under the hood) permits <span>/class, so this survives sanitization.
 */
function DetailsBoxRow({ rowKey }: { rowKey: TranslationKey }) {
	return (
		<li className="flex items-start gap-2">
			<CheckIcon size="x-small" className="text-icon-information mt-0.5 shrink-0" />
			<Text as="span" variant="body" size="small" className="[&_.example]:italic [&_.label]:font-bold">
				<TypedTranslateHtml value={rowKey} />
			</Text>
		</li>
	);
}

export const PersonalPrompt: React.FC = () => {
	const { typedTranslate } = useI18n();
	const { data, actions } = usePersonalPrompt();

	if (data.error) return <ErrorScreen />;

	if (data.loading) return <Loading />;

	const { variation } = data.experiment;
	const { copyKeys, showDetailsBox } = getVariantContent(variation);

	return (
		<div
			className="container mx-auto mb-22 flex max-w-[620px] flex-col gap-6 px-5 py-8 md:gap-12"
			data-qa="personal-prompt"
		>
			<div className="grid gap-4">
				<Text as="h1" variant="heading" className="m-0">
					<TypedTranslate value={copyKeys.heading} />
				</Text>

				<Text as="p" variant="body" size="large">
					<TypedTranslate value={copyKeys.description} />
				</Text>
			</div>

			<Form
				onSubmit={actions.submit}
				schema={data.schema}
				defaultValues={data.defaultValues}
				className="grid gap-6 md:gap-12"
				data-qa="issue-prompt-form"
			>
				<TextField
					name="personalStory"
					size="large"
					boldLabel
					maxLength={500}
					label={typedTranslate(copyKeys.inputLabel)}
					placeholder={typedTranslate(copyKeys.inputPlaceholder)}
					onBlur={actions.persistDraft}
					// autoResize (on by default) only grows the box in response to typing, not the static
					// placeholder — the default min-h-[84px] is a hair short for the Variant 1/2 placeholders'
					// 3 wrapped lines, which showed a scrollbar instead of growing. Raised only for those
					// variants (Control's placeholder already fits) so Control's layout stays unchanged.
					input={<TextArea className={cn({ 'min-h-[120px]': showDetailsBox })} />}
				/>

				{showDetailsBox && (
					<Tip size="small" data-qa="personal-prompt-details-box">
						{/* Icon+heading composed manually rather than via Tip's `icon`/`heading` props: Tip's
						    own layout is `flex-col md:flex-row`, which always stacks the icon above the
						    heading on mobile — Figma pairs them on one row at every width. `heading` is also
						    skipped on its own merits since Tip always renders it as an <h3>, which would skip
						    a level on a page whose only other heading is the step's <h1> — h2 here keeps
						    sequential order instead. The badge classes below mirror Tip's own icon-circle
						    styling (bg-fill-information-strong / text-icon-inverse-strong / size-7) so it
						    still matches Tip's built-in look. */}
						<div className="mb-2 flex items-center gap-2">
							<span className="bg-fill-information-strong text-icon-inverse-strong flex size-7 shrink-0 items-center justify-center rounded-full">
								<LightbulbOutlineIcon size="x-small" />
							</span>
							<Text as="h2" fontWeight="bold">
								<Translate value="corgi.start_a_petition.personal_prompt.details_box_heading" />
							</Text>
						</div>
						{/* ml-9 = the icon circle's width (size-7 = 28px) + the row's gap-2 (8px), so the
						    checklist aligns under the heading text rather than under the icon. */}
						<ul className="ml-9 grid gap-3">
							<DetailsBoxRow rowKey={translationKey('corgi.start_a_petition.personal_prompt.details_box_person')} />
							<DetailsBoxRow rowKey={translationKey('corgi.start_a_petition.personal_prompt.details_box_place')} />
							<DetailsBoxRow rowKey={translationKey('corgi.start_a_petition.personal_prompt.details_box_duration')} />
						</ul>
					</Tip>
				)}

				<StepActions type="form" onSecondaryAction={actions.back} />
			</Form>
		</div>
	);
};
